import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  TrendingUp as TrendingUpIcon,
  PieChart as PieChartIcon,
  Refresh as RefreshIcon,
  Group as GroupIcon,
  MonetizationOn as MonetizationOnIcon,
  Leaderboard as LeaderboardIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: 'auto',
  minHeight: '400px',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  border: '1px solid #f1f5f9',
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px 0 rgba(0,0,0,0.08)',
  },
}));

const ChartTitle = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '24px',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    width: '20px',
    height: '20px',
  },
  '& .MuiTypography-root': {
    fontSize: '1.1rem',
    fontWeight: 500,
    color: '#1e293b',
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.875rem',
  borderBottom: '1px solid #f1f5f9',
  padding: '16px',
  '&.MuiTableCell-head': {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
}));

const DashboardPage = () => {
  const [chartData, setChartData] = useState({
    revenueData: [],
    statusData: [],
    contactData: [],
    avgRevenueData: [],
    topInfluencers: [],
    outreachData: []
  });
  const [loading, setLoading] = useState(true);
  const [revenuePeriod, setRevenuePeriod] = useState('monthly');
  const [contactPeriod, setContactPeriod] = useState('monthly');
  const [data, setData] = useState(null);

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/data`);
      const apiData = await response.json();
      console.log('API Data:', apiData);
      setData(apiData);

      const transformedData = {
        revenueData: transformRevenueData(apiData.revenue?.[revenuePeriod] || {}),
        statusData: transformStatusData(apiData.status || {}),
        avgRevenueData: apiData.revenue?.averagePerPost || [],
        topInfluencers: apiData.influencers?.top10 || [],
        outreachData: apiData.outreach || []
      };

      console.log('Transformed Data:', transformedData);
      setChartData(transformedData);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setChartData({
        revenueData: [],
        statusData: [],
        avgRevenueData: [],
        topInfluencers: [],
        outreachData: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'rebooking': '#4ade80',
      'negotiations': '#fbbf24',
      'contract closed': '#60a5fa',
      'aftercare': '#a78bfa',
      'check posting': '#f472b6',
      'refused': '#ef4444',
      'waiting to post': '#38bdf8',
      'archived': '#94a3b8'
    };
    return colors[status.toLowerCase()] || '#cbd5e1';
  };

  const transformRevenueData = (revenueData) => {
    return Object.entries(revenueData)
      .map(([date, values]) => ({
        month: date,
        revenue: Number(values.revCode),
        newRevenue: Number(values.revCodeNew)
      }))
      .sort((a, b) => {
        // For weekly data, parse the week number
        if (a.month.includes('W')) {
          const [yearA, weekA] = a.month.split('-W').map(Number);
          const [yearB, weekB] = b.month.split('-W').map(Number);
          if (yearA === yearB) {
            return weekA - weekB;
          }
          return yearA - yearB;
        }
        // For other formats, use date comparison
        return new Date(a.month) - new Date(b.month);
      });
  };

  const transformStatusData = (statusData) => {
    // Calculate total active cases (excluding refused and archived)
    const activeTotal = Object.entries(statusData)
      .filter(([name]) => !['refused', 'archived'].includes(name.toLowerCase()))
      .reduce((sum, [_, value]) => sum + Number(value), 0);

    // Transform data and calculate percentages for active statuses
    return {
      activeStatuses: Object.entries(statusData)
        .filter(([name]) => !['refused', 'archived'].includes(name.toLowerCase()))
        .map(([name, value]) => ({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          value: Number(value),
          percentage: ((Number(value) / activeTotal) * 100).toFixed(1),
          fill: getStatusColor(name)
        })),
      refused: Number(statusData.refused || 0),
      archived: Number(statusData.archived || 0)
    };
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  const handleRevenuePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setRevenuePeriod(newPeriod);
      setChartData(prevData => ({
        ...prevData,
        revenueData: transformRevenueData(data.revenue?.[newPeriod] || {})
      }));
    }
  };

  const handleContactPeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setContactPeriod(newPeriod);
      // Here you would typically fetch new data based on the selected period
    }
  };

  useEffect(() => {
    if (data?.revenue?.averagePerPost) {
      setChartData(prevData => ({
        ...prevData,
        avgRevenueData: data.revenue.averagePerPost
      }));
    }
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      p: { xs: 2, md: 4 },
      bgcolor: '#ffffff',
      minHeight: '100vh',
      height: '100%',
      width: '100%',
      position: 'relative',
      pr: { xs: 4, md: 15 },
      pl: { xs: 2, md: 4 }
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4
      }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#0f172a'
          }}
        >
          Dashboard Overview
        </Typography>
        <IconButton
          onClick={fetchChartData}
          sx={{
            width: '40px',
            height: '40px',
            backgroundColor: '#f8fafc',
            '&:hover': {
              backgroundColor: '#f1f5f9',
              transform: 'rotate(45deg)',
              transition: 'transform 0.3s ease-in-out'
            }
          }}
        >
          <RefreshIcon sx={{ fontSize: '20px', color: '#64748b' }} />
        </IconButton>
      </Box>

      <Grid container spacing={3} sx={{ pr: 2 }}>
        {/* Revenue Chart */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <ChartTitle>
                <TrendingUpIcon sx={{ color: '#ff7c7c' }} />
                <Typography>Revenue / New Revenue</Typography>
              </ChartTitle>
              <ToggleButtonGroup
                value={revenuePeriod}
                exclusive
                onChange={handleRevenuePeriodChange}
                size="small"
              >
                <ToggleButton value="daily" sx={{ textTransform: 'none' }}>
                  Daily
                </ToggleButton>
                <ToggleButton value="weekly" sx={{ textTransform: 'none' }}>
                  Weekly
                </ToggleButton>
                <ToggleButton value="monthly" sx={{ textTransform: 'none' }}>
                  Monthly
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {chartData.revenueData && chartData.revenueData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData.revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="revenue"
                    fill="#ff7c7c"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="newRevenue"
                    fill="#ffd700"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </StyledPaper>
        </Grid>

        {/* Status Chart */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <ChartTitle>
                <PieChartIcon sx={{ color: '#8884d8' }} />
                <Typography>Active Barterdeals Status</Typography>
              </ChartTitle>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">Refused</Typography>
                  <Typography variant="h6" color="error.main">
                    {chartData.statusData?.refused || 0}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', ml: 3 }}>
                  <Typography variant="body2" color="text.secondary">Archived</Typography>
                  <Typography variant="h6" color="text.secondary">
                    {chartData.statusData?.archived || 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {chartData.statusData?.activeStatuses?.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={chartData.statusData.activeStatuses}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={70}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);

                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#64748b"
                          fontSize={11}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="middle"
                        >
                          {`${(percent * 100).toFixed(1)}%`}
                        </text>
                      );
                    }}
                    labelLine={{
                      stroke: '#e2e8f0',
                      strokeWidth: 1
                    }}
                  >
                    {chartData.statusData.activeStatuses.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: '11px'
                    }}
                    formatter={(value, name, props) => [
                      `${value} (${props.payload.percentage}%)`,
                      name
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </StyledPaper>
        </Grid>

        {/* Contacted and Interested Chart */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <ChartTitle>
                <GroupIcon sx={{ color: '#82ca9d' }} />
                <Typography>Contacted and Interested</Typography>
              </ChartTitle>
            </Box>
            {chartData.outreachData && chartData.outreachData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart
                  data={[...chartData.outreachData].reverse()}
                  margin={{ top: 10, right: 30, left: 30, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorContacted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.4}/>
                      <stop offset="50%" stopColor="#60a5fa" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.02}/>
                    </linearGradient>
                    <linearGradient id="colorInterested" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#82ca9d" stopOpacity={0.4}/>
                      <stop offset="50%" stopColor="#82ca9d" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.02}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      return `${month}/${year.slice(2)}`;
                    }}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                    formatter={(value, name, props) => {
                      const percentage = ((props.payload.interested / props.payload.contacted) * 100).toFixed(1);
                      return [
                        value,
                        name === 'interested' ? `${name} (${percentage}%)` : name
                      ];
                    }}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-');
                      return `${month}/${year}`;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="contacted"
                    stroke="#60a5fa"
                    strokeWidth={2}
                    fill="url(#colorContacted)"
                    dot={{
                      stroke: '#60a5fa',
                      strokeWidth: 2,
                      fill: '#fff',
                      r: 4
                    }}
                    activeDot={{
                      stroke: '#60a5fa',
                      strokeWidth: 2,
                      fill: '#fff',
                      r: 6
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="interested"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    fill="url(#colorInterested)"
                    dot={{
                      stroke: '#82ca9d',
                      strokeWidth: 2,
                      fill: '#fff',
                      r: 4
                    }}
                    activeDot={{
                      stroke: '#82ca9d',
                      strokeWidth: 2,
                      fill: '#fff',
                      r: 6
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </StyledPaper>
        </Grid>
        {/* Avg. Revenue per Post Chart */}
        <Grid item xs={12} md={6}>
          <StyledPaper>
            <ChartTitle>
              <MonetizationOnIcon sx={{ color: '#82ca9d' }} />
              <Typography>Avg. Revenue per Post - Monthly View</Typography>
            </ChartTitle>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart
                data={[...chartData.avgRevenueData].reverse()}
                margin={{ top: 10, right: 30, left: 30, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#82ca9d" stopOpacity={0.4}/>
                    <stop offset="50%" stopColor="#82ca9d" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f1f5f9"
                />
                <XAxis
                  dataKey="month"
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => {
                    const [year, month] = value.split('-');
                    return `${month}/${year.slice(2)}`;
                  }}
                />
                <YAxis
                  stroke="#94a3b8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => `€${value.toLocaleString()}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [`€${value.toLocaleString()}`, 'Avg. Revenue per Post']}
                  labelFormatter={(label) => {
                    const [year, month] = label.split('-');
                    return `${month}/${year}`;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="averageRevenuePerPost"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  fill="url(#colorRevenue)"
                  dot={{
                    stroke: '#82ca9d',
                    strokeWidth: 2,
                    fill: '#fff',
                    r: 4
                  }}
                  activeDot={{
                    stroke: '#82ca9d',
                    strokeWidth: 2,
                    fill: '#fff',
                    r: 6
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </StyledPaper>
        </Grid>

        {/* Top 10 Influencers Table */}
        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <ChartTitle>
                <LeaderboardIcon sx={{ color: '#6366f1' }} />
                <Typography>Top 10 Influencers by Revenue</Typography>
              </ChartTitle>
              <Button
                component={Link}
                to="/influencers"
                variant="text"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  color: '#6366f1',
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.04)',
                  }
                }}
              >
                View All
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Rank</StyledTableCell>
                    <StyledTableCell>Influencer</StyledTableCell>
                    <StyledTableCell align="right">Revenue (€)</StyledTableCell>
                    <StyledTableCell align="right">New Revenue (€)</StyledTableCell>
                    <StyledTableCell align="right">Orders</StyledTableCell>
                    <StyledTableCell align="right">Posts</StyledTableCell>
                    <StyledTableCell align="right">AOV (€)</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.topInfluencers.map((influencer, index) => (
                    <TableRow
                      key={influencer.name}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                        },
                        ...(index < 3 && {
                          backgroundColor: '#f8fafc',
                          '& .MuiTableCell-root': {
                            fontWeight: 600,
                          }
                        })
                      }}
                    >
                      <StyledTableCell>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {index < 3 ? (
                            <Box sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              backgroundColor:
                                index === 0 ? '#ffd700' :
                                index === 1 ? '#c0c0c0' :
                                '#cd7f32'
                            }}>
                              {index + 1}
                            </Box>
                          ) : (
                            index + 1
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {influencer.name}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="right" sx={{ color: '#16a34a' }}>
                        {new Intl.NumberFormat('de-DE').format(influencer.revenue)}
                      </StyledTableCell>
                      <StyledTableCell align="right" sx={{ color: '#2563eb' }}>
                        {new Intl.NumberFormat('de-DE').format(influencer.newRevenue)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE').format(influencer.totalOrders)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {influencer.posts}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2 }).format(influencer.aov)}
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledPaper>
        </Grid>


      </Grid>
    </Box>
  );
};

export default DashboardPage;